<template>
  <v-card data-testid="eudr-detail">
    <v-card-title class="primary white--text">
      <span class="headline text-truncate">{{ title || $t('eudrTractDetails') }}</span>
      <v-spacer/>
      <Icon
        dataTestId="eudr-detail-close"
        icon="mdi-close"
        iconColor="white"
        :tooltipText="$t('close')"
        :small="false"
        :large="true"
        @icon-clicked="$emit('close')"
      />
    </v-card-title>
    <v-card-text class="pa-0">
      <!-- Composite preview: the accounts/reports this composite was built from. -->
      <div v-if="sources.length" class="px-4 pt-3">
        <span class="caption grey--text text--darken-1">{{ $t('eudrBuiltFrom') }}</span>
        <div class="mt-1">
          <v-chip
            v-for="(source, index) in sources"
            :key="`eudr-source-${index}`"
            small
            class="mr-1 mb-1"
          >
            <v-icon left small>{{ source.sourceAccountId ? 'mdi-home' : 'mdi-domain' }}</v-icon>
            {{ source.name || $t('untitled') }}
          </v-chip>
        </div>
      </div>
      <v-row no-gutters>
        <v-col cols="12" md="5">
          <div class="eudr-detail-table-wrap">
            <DataTable
              :items="tracts"
              :headers="headers"
              :customCells="customCells"
              tableKey="tractName"
              defaultSort="conflict"
              :sortDesc="true"
              :showSearch="true"
            >
              <template #tractNameCell="{ item }">
                <span class="d-flex align-center eudr-detail-name" @click="zoomToRow(item)">
                  <v-icon v-if="item.conflict" small color="warning" class="mr-1">mdi-alert</v-icon>
                  {{ item.tractName }}
                </span>
              </template>
            </DataTable>
          </div>
        </v-col>
        <v-col cols="12" md="7">
          <EudrMap
            v-if="hasGeometry"
            ref="map"
            :featureCollection="featureCollection"
          />
          <div v-else class="d-flex align-center justify-center grey--text text--darken-1" style="height: 70vh;">
            {{ $t('eudrNoGeometry') }}
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { isSyntheticParcelId } from '@/utils/eudr/EudrFeatureName.js'

export default {
  name: 'EudrDetailDialog',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    EudrMap: () => import('@/components/eudr/EudrMap.vue')
  },

  props: {
    // Per-tract rows: { tractName, parcelId, conflict }.
    tracts: {
      type: Array,
      required: true
    },
    // GeoJSON FeatureCollection of every tract polygon (frozen by the caller).
    featureCollection: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    // Composite-only: the constituent sources, each { sourceBatchId?, sourceAccountId?, name }. Empty otherwise.
    sources: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  computed: {
    hasGeometry () {
      return (this.featureCollection?.features?.length ?? 0) > 0
    },

    customCells () {
      return [{ value: 'tractName', slotName: 'tractNameCell' }]
    },

    headers () {
      const headers = [
        { text: this.$t('name'), value: 'tractName', sortable: true, align: 'start' }
      ]
      // CSV point uploads carry only synthetic point:<lon>,<lat> ids — hide the column when no real parcel number exists.
      if (this.tracts.some(t => t.parcelId && !isSyntheticParcelId(t.parcelId))) {
        headers.push({ text: this.$t('parcelNumber'), value: 'parcelId', sortable: true, align: 'start', format: v => (v && !isSyntheticParcelId(v)) ? v : this.$t('notAvailable') })
      }
      return headers
    }
  },

  methods: {
    zoomToRow (item) {
      if (!item.features?.length || !this.$refs.map) return
      this.$refs.map.zoomToGeojson({ type: 'FeatureCollection', features: item.features })
    }
  }
}
</script>

<style scoped>
.eudr-detail-table-wrap {
  height: 70vh;
  overflow-y: auto;
}
.eudr-detail-name {
  cursor: pointer;
}
.eudr-detail-name:hover {
  text-decoration: underline;
}
</style>
